import { posthog } from '@pique/core/posthog';
import { WorkspaceFlavour } from '@pique/env/workspace';
import { ErrorNames, PublicPageMode, UserFriendlyError } from '@pique/graphql';
import type { Workspace, WorkspacesService } from '@toeverything/infra';
import { DocsService } from '@toeverything/infra';

import { getShareUrl } from '../../cloud/services/fetch';
import { WorkspacePropertiesAdapter } from '../../properties';
import {
  type CloudWorkspaceReadyInfo,
  copyPageProperties,
  ensureCloudWorkspaceReadyForDocCreation,
  findOrCreateNamedCloudWorkspace,
  syncDocContent,
} from './cloud-sync-utils';
import { ShareInfoService } from './share-info';

export interface LocalShareResult {
  shareUrl: string;
  cloudWorkspaceId: string;
  cloudDocId: string;
}

export interface LocalShareMapping {
  localWorkspaceId: string;
  localDocId: string;
  cloudWorkspaceId: string;
  cloudDocId: string;
  shareUrl: string;
  createdAt: number;
}

const SHARED_WORKSPACE_NAME = 'Shared Notes';
const LOCAL_SHARE_MAPPINGS_KEY = 'pique:local-share-mappings';

function getLocalShareMappings(): LocalShareMapping[] {
  try {
    const stored = localStorage.getItem(LOCAL_SHARE_MAPPINGS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveLocalShareMappings(mappings: LocalShareMapping[]): void {
  try {
    localStorage.setItem(LOCAL_SHARE_MAPPINGS_KEY, JSON.stringify(mappings));
  } catch {}
}

export function getLocalDocShareInfo(
  localWorkspaceId: string,
  localDocId: string
): LocalShareMapping | null {
  const mappings = getLocalShareMappings();
  return (
    mappings.find(
      m =>
        m.localWorkspaceId === localWorkspaceId && m.localDocId === localDocId
    ) ?? null
  );
}

function addLocalShareMapping(mapping: LocalShareMapping): void {
  const mappings = getLocalShareMappings();
  const existingIndex = mappings.findIndex(
    m =>
      m.localWorkspaceId === mapping.localWorkspaceId &&
      m.localDocId === mapping.localDocId
  );
  if (existingIndex >= 0) {
    mappings[existingIndex] = mapping;
  } else {
    mappings.push(mapping);
  }
  saveLocalShareMappings(mappings);
}

function removeLocalShareMapping(
  localWorkspaceId: string,
  localDocId: string
): void {
  const mappings = getLocalShareMappings();
  const filtered = mappings.filter(
    m =>
      !(m.localWorkspaceId === localWorkspaceId && m.localDocId === localDocId)
  );
  saveLocalShareMappings(filtered);
}

function isIgnorableStopShareError(error: unknown): boolean {
  const friendly = UserFriendlyError.fromAnyError(error);
  const ignorableNames = new Set<ErrorNames>([
    ErrorNames.SPACE_ACCESS_DENIED,
    ErrorNames.DOC_ACCESS_DENIED,
    ErrorNames.SPACE_NOT_FOUND,
    ErrorNames.DOC_NOT_FOUND,
    ErrorNames.NOT_IN_SPACE,
    ErrorNames.AUTHENTICATION_REQUIRED,
    ErrorNames.ACCESS_DENIED,
    ErrorNames.ACTION_FORBIDDEN,
  ]);
  if (ignorableNames.has(friendly.name)) {
    return true;
  }

  const message = error instanceof Error ? error.message : String(error ?? '');
  const normalized = message.toLowerCase();
  return (
    normalized.includes('permission to access space') ||
    normalized.includes('space access denied') ||
    normalized.includes('doc access denied') ||
    normalized.includes('doc record not found') ||
    normalized.includes('doc not found') ||
    normalized.includes('authentication required')
  );
}

export async function shareLocalDoc(
  workspacesService: WorkspacesService,
  sourceWorkspace: Workspace,
  sourceDocId: string
): Promise<LocalShareResult> {
  const sourceDoc = sourceWorkspace.docCollection.getDoc(sourceDocId);
  if (!sourceDoc) {
    posthog.captureException(new Error('Source doc not found for sharing'), {
      context: 'shareLocalDoc',
      step: 'source_validation',
      sourceWorkspaceId: sourceWorkspace.id,
      sourceDocId,
    });
    throw new Error('Failed to share. Please try again.');
  }

  const existingShare = getLocalDocShareInfo(sourceWorkspace.id, sourceDocId);
  if (existingShare) {
    try {
      const { workspace: cloudWorkspace, dispose } = workspacesService.open({
        metadata: {
          id: existingShare.cloudWorkspaceId,
          flavour: WorkspaceFlavour.PIQUE_CLOUD,
        },
      });

      try {
        await ensureCloudWorkspaceReadyForDocCreation(cloudWorkspace);
        const docsService = cloudWorkspace.scope.get(DocsService);
        const { doc: openedDoc, release } = docsService.open(
          existingShare.cloudDocId
        );

        try {
          await openedDoc.waitForSyncReady();
          const shareInfoService = openedDoc.scope.get(ShareInfoService);
          await shareInfoService.shareInfo.enableShare(PublicPageMode.Page);
          await cloudWorkspace.engine.waitForGracefulStop();
        } finally {
          release();
        }
      } finally {
        dispose();
      }

      return {
        shareUrl: getShareUrl(
          existingShare.cloudWorkspaceId,
          existingShare.cloudDocId
        ),
        cloudWorkspaceId: existingShare.cloudWorkspaceId,
        cloudDocId: existingShare.cloudDocId,
      };
    } catch (error) {
      removeLocalShareMapping(sourceWorkspace.id, sourceDocId);
    }
  }

  const docTitle = sourceDoc.meta?.title || 'Shared Note';
  const cloudWorkspaceMetadata = await findOrCreateNamedCloudWorkspace(
    workspacesService,
    SHARED_WORKSPACE_NAME
  );

  const { workspace: cloudWorkspace, dispose } = workspacesService.open({
    metadata: cloudWorkspaceMetadata,
  });

  let cloudDocId: string | null = null;
  let release: (() => void) | null = null;
  let step = 'init';
  let readiness: CloudWorkspaceReadyInfo | null = null;

  try {
    step = 'waiting_for_root_ready';
    readiness = await ensureCloudWorkspaceReadyForDocCreation(cloudWorkspace);

    step = 'creating_cloud_doc';
    const docsService = cloudWorkspace.scope.get(DocsService);

    let cloudDocRecord: ReturnType<typeof docsService.createDoc> | null = null;
    let createAttempts = 0;
    const maxAttempts = 3;

    while (!cloudDocRecord && createAttempts < maxAttempts) {
      createAttempts++;
      try {
        if (createAttempts > 1) {
          await new Promise(resolve =>
            setTimeout(resolve, 200 * createAttempts)
          );
        }
        cloudDocRecord = docsService.createDoc({
          title: docTitle,
          initialize: false,
        });
      } catch (createError) {
        if (createAttempts >= maxAttempts) {
          posthog.captureException(createError, {
            context: 'shareLocalDoc_createDoc',
            attempt: createAttempts,
            cloudWorkspaceId: cloudWorkspaceMetadata.id,
            ...readiness,
          });
          throw createError;
        }
      }
    }

    if (!cloudDocRecord) {
      posthog.captureException(
        new Error('Cloud doc creation failed after retries'),
        {
          context: 'shareLocalDoc',
          step: 'creating_cloud_doc',
          attempts: createAttempts,
          cloudWorkspaceId: cloudWorkspaceMetadata.id,
          sourceDocId,
          ...readiness,
        }
      );
      throw new Error('Failed to share. Please try again.');
    }

    const docId = cloudDocRecord.id;
    cloudDocId = docId;

    step = 'opening_cloud_doc';
    const openResult = docsService.open(docId);
    release = openResult.release;
    const openedDoc = openResult.doc;

    step = 'waiting_for_doc_ready';
    await openedDoc.waitForSyncReady();

    const cloudDoc = cloudWorkspace.docCollection.getDoc(docId);
    if (!cloudDoc) {
      posthog.captureException(
        new Error('Failed to resolve cloud doc after creation'),
        {
          context: 'shareLocalDoc',
          step: 'resolving_cloud_doc',
          cloudDocId: docId,
          cloudWorkspaceId: cloudWorkspaceMetadata.id,
          sourceDocId,
          ...readiness,
        }
      );
      throw new Error('Failed to share. Please try again.');
    }

    step = 'syncing_content';
    syncDocContent(sourceDoc, cloudDoc);

    if (cloudDoc.meta) {
      cloudDoc.meta.title = docTitle;
    }

    try {
      step = 'copying_properties';
      await copyPageProperties(
        sourceWorkspace,
        cloudWorkspace,
        sourceDocId,
        docId,
        docTitle
      );

      const sourcePropertiesAdapter = sourceWorkspace.scope.get(
        WorkspacePropertiesAdapter
      );
      const cloudPropertiesAdapter = cloudWorkspace.scope.get(
        WorkspacePropertiesAdapter
      );
      const srcProps = sourcePropertiesAdapter.getPageProperties(sourceDocId);
      const dstProps = cloudPropertiesAdapter.getPageProperties(docId);
      if (srcProps && dstProps) {
        const customEntries = Object.entries(srcProps.custom || {});
        for (const [propId, prop] of customEntries) {
          if (prop && typeof prop === 'object') {
            (dstProps.custom as Record<string, unknown>)[propId] = { ...prop };
          }
        }
      }
    } catch {}

    step = 'waiting_for_sync';
    await cloudWorkspace.engine.waitForDocSynced();

    step = 'enabling_share';
    const shareInfoService = openedDoc.scope.get(ShareInfoService);
    await shareInfoService.shareInfo.enableShare(PublicPageMode.Page);

    step = 'graceful_stop';
    await cloudWorkspace.engine.waitForGracefulStop();

    if (release) {
      release();
      release = null;
    }
    dispose();

    const shareUrl = getShareUrl(cloudWorkspaceMetadata.id, docId);

    addLocalShareMapping({
      localWorkspaceId: sourceWorkspace.id,
      localDocId: sourceDocId,
      cloudWorkspaceId: cloudWorkspaceMetadata.id,
      cloudDocId: docId,
      shareUrl,
      createdAt: Date.now(),
    });

    posthog.track('share_local_doc_success', {
      localWorkspaceId: sourceWorkspace.id,
      localDocId: sourceDocId,
      cloudWorkspaceId: cloudWorkspaceMetadata.id,
      cloudDocId: docId,
    });

    return {
      shareUrl,
      cloudWorkspaceId: cloudWorkspaceMetadata.id,
      cloudDocId: docId,
    };
  } catch (error) {
    posthog.captureException(error, {
      context: 'shareLocalDoc',
      step,
      localWorkspaceId: sourceWorkspace.id,
      localDocId: sourceDocId,
      cloudWorkspaceId: cloudWorkspaceMetadata?.id,
      cloudDocId,
      errorMessage: error instanceof Error ? error.message : String(error),
      ...readiness,
    });

    if (release) {
      try {
        release();
      } catch {}
    }
    dispose();

    if (cloudDocId) {
      try {
        cloudWorkspace.docCollection.removeDoc(cloudDocId);
      } catch {}
    }

    throw error;
  }
}

export async function stopSharingLocalDoc(
  workspacesService: WorkspacesService,
  localWorkspaceId: string,
  localDocId: string
): Promise<void> {
  const mapping = getLocalDocShareInfo(localWorkspaceId, localDocId);
  if (!mapping) {
    return;
  }

  const { workspace: cloudWorkspace, dispose } = workspacesService.open({
    metadata: {
      id: mapping.cloudWorkspaceId,
      flavour: WorkspaceFlavour.PIQUE_CLOUD,
    },
  });

  let shouldRemoveMapping = false;
  let release: (() => void) | null = null;
  let readiness: CloudWorkspaceReadyInfo | null = null;

  try {
    readiness = await ensureCloudWorkspaceReadyForDocCreation(cloudWorkspace);
    const docsService = cloudWorkspace.scope.get(DocsService);
    const openResult = docsService.open(mapping.cloudDocId);
    const openedDoc = openResult.doc;
    release = openResult.release;

    await openedDoc.waitForSyncReady();

    const shareInfoService = openedDoc.scope.get(ShareInfoService);
    await shareInfoService.shareInfo.disableShare();

    await cloudWorkspace.engine.waitForGracefulStop();

    if (release) {
      release();
      release = null;
    }
    shouldRemoveMapping = true;
  } catch (error) {
    const ignorable = isIgnorableStopShareError(error);
    if (ignorable) {
      // Ignored
    } else {
      posthog.captureException(error, {
        context: 'stopSharingLocalDoc',
        ...readiness,
      });
      throw error;
    }
    shouldRemoveMapping = true;
  } finally {
    if (release) {
      try {
        release();
      } catch {}
    }
    dispose();
  }

  if (shouldRemoveMapping) {
    removeLocalShareMapping(localWorkspaceId, localDocId);
  }
}

export function generateShareUrl(
  workspaceId: string,
  docId: string,
  mode?: 'page' | 'edgeless'
): string | null {
  const baseUrl = getPiqueCloudBaseUrl();
  if (!baseUrl) return null;

  try {
    const url = new URL(`${baseUrl}/share/${workspaceId}/${docId}`);
    if (mode) {
      url.searchParams.append('mode', mode);
    }
    return url.toString();
  } catch {
    return null;
  }
}

function getPiqueCloudBaseUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return window.location.origin;
}
